import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import SignaturePad from "../components/SignaturePad";
import { ShieldAlert, ArrowLeft, FileText, Download, CheckCircle2, AlertTriangle, Upload, PenTool } from "lucide-react";

const STATUS_PILL = {
  Draft:           { bg: "#fef3c7", color: "#92400e", border: "#f59e0b" },
  Stage1Signed:    { bg: "#dbeafe", color: "#1e40af", border: "#3b82f6" },
  Stage2Approved:  { bg: "#e0e7ff", color: "#3730a3", border: "#6366f1" },
  DRASigned:       { bg: "#d1fae5", color: "#065f46", border: "#059669" },
  Superseded:      { bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1" },
};

function StatusPill({ status, testid }) {
  const s = STATUS_PILL[status] || STATUS_PILL.Draft;
  return (
    <span
      data-testid={testid}
      className="mono"
      style={{
        display: "inline-block", padding: "3px 10px", fontSize: 11, fontWeight: 700,
        background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 2,
        letterSpacing: "0.06em",
      }}
    >{status?.toUpperCase()}</span>
  );
}

function HazardsTable({ rams }) {
  const reviewSet = new Set(rams.review_required_hazard_ids || []);
  const BAND = {
    H: { bg: "#fef2f2", text: "#991b1b", border: "#dc2626" },
    M: { bg: "#fffbeb", text: "#92400e", border: "#f59e0b" },
    L: { bg: "#ecfdf5", text: "#065f46", border: "#059669" },
  };
  const chip = (b, key) => {
    const cc = BAND[b?.band] || BAND.L;
    return <span key={key} className="mono" style={{ display: "inline-block", padding: "3px 8px", fontSize: 10, fontWeight: 700, background: cc.bg, color: cc.text, border: `1px solid ${cc.border}`, borderRadius: 2, whiteSpace: "nowrap" }}>{b.L}×{b.S}={b.R} ({b.band})</span>;
  };
  return (
    <div className="card" style={{ padding: 0 }} data-testid="rams-detail-hazards">
      <table>
        <thead><tr><th>#</th><th>Hazard</th><th>At risk</th><th>Initial</th><th>Controls</th><th>Residual</th></tr></thead>
        <tbody>
          {(rams.selected_hazards || []).map((h) => {
            const bullets = (h.controls || "").split(";").map((s) => s.trim()).filter(Boolean);
            return (
              <tr key={h.id} data-testid={`detail-hazard-${h.id}`}>
                <td className="mono" style={{ fontWeight: 700, verticalAlign: "top" }}>{h.id}</td>
                <td style={{ verticalAlign: "top" }}>
                  <div style={{ fontWeight: 600 }}>{h.name}</div>
                  {reviewSet.has(h.id) ? (
                    <div className="mono" style={{ fontSize: 10, color: "#b91c1c", marginTop: 2 }}>
                      <AlertTriangle size={10} style={{ verticalAlign: -1, marginRight: 2 }} />Review required
                    </div>
                  ) : null}
                </td>
                <td className="mono" style={{ fontSize: 11, color: "#475569", verticalAlign: "top" }}>{(h.persons_at_risk || []).join(", ")}</td>
                <td style={{ verticalAlign: "top" }}>{chip(h.initial, "i")}</td>
                <td style={{ verticalAlign: "top", minWidth: 260 }}>
                  {bullets.length <= 1 ? <div style={{ fontSize: 12 }}>{h.controls}</div> :
                    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, lineHeight: 1.5 }}>{bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>}
                </td>
                <td style={{ verticalAlign: "top" }}>{chip(h.residual, "r")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SigBlock({ title, sub, sig }) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.6 }}>
        {sub.map(([k, v]) => v ? <div key={k}><span className="mono" style={{ color: "#94a3b8" }}>{k}:</span> <b>{v}</b></div> : null)}
      </div>
      {sig ? (
        <div style={{ marginTop: 10, border: "1px solid #e2e8f0", padding: 6, background: "white", maxWidth: 320 }}>
          <img src={sig} alt="signature" style={{ maxWidth: "100%", maxHeight: 80 }} />
        </div>
      ) : <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>— pending —</div>}
    </div>
  );
}

function Stage1Panel({ rams, onSigned }) {
  const [operatives, setOperatives] = useState([]);
  const [opId, setOpId] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/operatives").then((r) => {
      const eligible = (r.data || []).filter((o) => o.rams_signoff_authority && o.status === "active");
      setOperatives(eligible);
      if (eligible.length) setOpId(eligible[0].id);
    }).catch(() => { /* operatives forbidden for some roles */ });
  }, []);

  const sign = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await api.post(`/rams/${rams.id}/stage1-sign`, { operative_id: opId, signature_data_url: sig });
      onSigned(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 16 }} data-testid="stage1-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <PenTool size={16} />
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Stage 1 — On-site authorisation</h3>
      </div>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>
        Pick an operative with RAMS sign-off authority. Their competence basis prints on the PDF. Preparer cannot be the stage-1 signer.
      </div>
      <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>Authorised operative</label>
      <select data-testid="stage1-operative" className="input-base" value={opId} onChange={(e) => setOpId(e.target.value)}>
        {operatives.length === 0 ? <option value="">— no authorised operatives —</option> : null}
        {operatives.map((o) => <option key={o.id} value={o.id}>{o.full_name} · {o.competence_basis || o.scaffolder_grade || o.op_type}</option>)}
      </select>
      <div style={{ marginTop: 10 }}>
        <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>Signature</label>
        <SignaturePad value={sig} onChange={setSig} testid="stage1-sig" />
      </div>
      {err ? <div className="badge badge-red" style={{ marginTop: 8, padding: "6px 10px" }} data-testid="stage1-err">{err}</div> : null}
      <button data-testid="stage1-submit" className="btn-primary" style={{ marginTop: 10 }} disabled={!opId || !sig || busy} onClick={sign}>
        {busy ? "Signing…" : "Apply Stage 1 sign-off"}
      </button>
    </div>
  );
}

function Stage2Panel({ rams, onReviewed, role }) {
  const [sig, setSig] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const canReview = role === "admin" || role === "competent_person";
  if (!canReview) {
    return (
      <div className="card" style={{ padding: 14 }} data-testid="stage2-locked">
        <div className="mono" style={{ color: "#64748b", fontSize: 12 }}>Stage 2 is locked — awaiting competent-person review.</div>
      </div>
    );
  }
  const review = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await api.post(`/rams/${rams.id}/stage2-review`, { signature_data_url: sig, comment });
      onReviewed(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="card" style={{ padding: 16 }} data-testid="stage2-panel">
      <h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700 }}>Stage 2 — Competent-person review</h3>
      <textarea data-testid="stage2-comment" className="input-base" rows={2} placeholder="Optional comment / amendments" value={comment} onChange={(e) => setComment(e.target.value)} />
      <div style={{ marginTop: 10 }}>
        <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>Reviewer signature</label>
        <SignaturePad value={sig} onChange={setSig} testid="stage2-sig" />
      </div>
      {err ? <div className="badge badge-red" style={{ marginTop: 8, padding: "6px 10px" }} data-testid="stage2-err">{err}</div> : null}
      <button data-testid="stage2-submit" className="btn-primary" style={{ marginTop: 10 }} disabled={!sig || busy} onClick={review}>
        {busy ? "Reviewing…" : "Apply Stage 2 review"}
      </button>
    </div>
  );
}

function DRAPanel({ rams, onSigned, role }) {
  const [mode, setMode] = useState("pad"); // 'pad' | 'upload'
  const [sig, setSig] = useState("");
  const [upload, setUpload] = useState("");
  const [byName, setByName] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const allowed = ["admin", "contracts_manager", "mccain", "mccain_external"].includes(role);
  if (!allowed) {
    return <div className="card" style={{ padding: 14 }} data-testid="dra-locked"><div className="mono" style={{ color: "#64748b", fontSize: 12 }}>DRA sign-off is locked for your role.</div></div>;
  }
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setUpload(r.result);
    r.readAsDataURL(f);
  };
  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await api.post(`/rams/${rams.id}/dra-sign`, {
        type: mode, by_name: byName,
        signature_data_url: mode === "pad" ? sig : null,
        upload_data_url: mode === "upload" ? upload : null,
        comment,
      });
      onSigned(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="card" style={{ padding: 16 }} data-testid="dra-panel">
      <h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700 }}>DRA — McCain Designated Responsible Authority</h3>
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <button type="button" data-testid="dra-mode-pad" onClick={() => setMode("pad")} className={mode === "pad" ? "btn-primary" : "btn-ghost"} style={{ padding: "6px 12px", fontSize: 12 }}><PenTool size={12} /> Sign in app</button>
        <button type="button" data-testid="dra-mode-upload" onClick={() => setMode("upload")} className={mode === "upload" ? "btn-primary" : "btn-ghost"} style={{ padding: "6px 12px", fontSize: 12 }}><Upload size={12} /> Upload signed PDF</button>
      </div>
      <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>Signer name</label>
      <input data-testid="dra-by-name" className="input-base" value={byName} onChange={(e) => setByName(e.target.value)} placeholder="e.g. John Doe, McCain DRA" />
      {mode === "pad" ? (
        <div style={{ marginTop: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>DRA signature</label>
          <SignaturePad value={sig} onChange={setSig} testid="dra-sig" />
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>Upload signed file (PDF / PNG / JPG)</label>
          <input data-testid="dra-upload" type="file" accept="application/pdf,image/*" onChange={onFile} />
          {upload ? <div className="mono" style={{ fontSize: 11, color: "#059669", marginTop: 4 }}>✓ file attached ({Math.round((upload.length * 3 / 4) / 1024)}KB)</div> : null}
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>Comment (optional)</label>
        <textarea data-testid="dra-comment" className="input-base" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
      </div>
      {err ? <div className="badge badge-red" style={{ marginTop: 8, padding: "6px 10px" }} data-testid="dra-err">{err}</div> : null}
      <button data-testid="dra-submit" className="btn-primary" style={{ marginTop: 10 }} disabled={!byName || (mode === "pad" ? !sig : !upload) || busy} onClick={submit}>
        {busy ? "Signing…" : "Apply DRA sign-off"}
      </button>
    </div>
  );
}

export default function RAMSDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rams, setRAMS] = useState(null);
  const [err, setErr] = useState("");

  const reload = () => api.get(`/rams/${id}`).then((r) => setRAMS(r.data)).catch((e) => setErr(e.response?.data?.detail || e.message));
  useEffect(() => { reload(); }, [id]);

  const downloadPdf = () => {
    const url = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/rams/${id}/pdf`;
    window.open(url, "_blank");
  };

  if (err) return <div style={{ padding: 24 }}><div className="badge badge-red" style={{ padding: "8px 12px" }} data-testid="rams-detail-err">{err}</div></div>;
  if (!rams) return <div style={{ padding: 24 }} className="mono">Loading…</div>;

  const status = rams.status;
  const intake = rams.intake || {};

  return (
    <div data-testid="rams-detail-page" style={{ padding: "24px 24px 64px" }}>
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <Link to="/rams" data-testid="back-to-rams" style={{ textDecoration: "none", color: "#475569", fontSize: 13 }}><ArrowLeft size={14} style={{ verticalAlign: -2 }} /> Back to RAMS</Link>
        <div style={{ display: "flex", gap: 8 }}>
          <button data-testid="rams-pdf-btn" className="btn-ghost" onClick={downloadPdf}><Download size={14} style={{ verticalAlign: -2 }} /> PDF</button>
        </div>
      </div>

      <header style={{ marginBottom: 18 }}>
        <div className="mono" style={{ color: "var(--brand-accent, #F05A14)", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>COMPLIANCE · RAMS</div>
        <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 0" }}>
          <ShieldAlert size={22} style={{ verticalAlign: -3, marginRight: 8 }} />
          {rams.structure_description || "RAMS"}
        </h1>
        <div style={{ marginTop: 8, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <StatusPill status={status} testid="rams-status-pill" />
          <span className="mono" style={{ color: "#64748b", fontSize: 12 }}>v{rams.version}</span>
          <span className="mono" style={{ color: "#64748b", fontSize: 12 }}>Job ref: {intake.job_ref || "—"}</span>
          <span className="mono" style={{ color: "#64748b", fontSize: 12 }}>Library v{rams.library_version} · master v{rams.library_source_master_version}</span>
        </div>
      </header>

      {/* Status timeline */}
      <div className="card" style={{ padding: 14, marginBottom: 16, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }} data-testid="rams-timeline">
        {[
          { key: "Draft", label: "1. Draft", at: rams.prepared_by?.at, who: rams.prepared_by?.name },
          { key: "Stage1Signed", label: "2. Stage 1", at: rams.stage1_sign?.at, who: rams.stage1_sign?.operative_name },
          { key: "Stage2Approved", label: "3. Stage 2", at: rams.stage2_review?.at, who: rams.stage2_review?.name },
          { key: "DRASigned", label: "4. DRA", at: rams.dra_sign?.at, who: rams.dra_sign?.by_name },
        ].map((step) => {
          const done = !!step.at;
          return (
            <div key={step.key} data-testid={`timeline-${step.key}`} style={{ borderTop: `3px solid ${done ? "#059669" : "#e2e8f0"}`, paddingTop: 8 }}>
              <div className="mono" style={{ fontSize: 10, color: done ? "#065f46" : "#94a3b8", fontWeight: 700, letterSpacing: "0.06em" }}>{step.label}</div>
              <div style={{ fontSize: 12, color: "#0f172a", marginTop: 2 }}>{step.who || "—"}</div>
              <div className="mono" style={{ fontSize: 10, color: "#94a3b8" }}>{step.at ? step.at.slice(0, 19).replace("T", " ") : "pending"}</div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 360px", gap: 18 }}>
        <div>
          <div className="card" style={{ padding: 14, marginBottom: 14 }}>
            <h2 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569" }}>Job &amp; intake</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, fontSize: 13 }}>
              <div><span className="mono" style={{ color: "#94a3b8" }}>Structure ref</span><br /><b>{rams.structure_ref || "—"}</b></div>
              <div><span className="mono" style={{ color: "#94a3b8" }}>Job ref</span><br /><b>{intake.job_ref || "—"}</b></div>
              <div><span className="mono" style={{ color: "#94a3b8" }}>McCain PO / permit</span><br /><b>{intake.mccain_po_or_permit_ref || "—"}</b></div>
              <div><span className="mono" style={{ color: "#94a3b8" }}>Area / line</span><br /><b>{intake.area_or_line || "—"}</b></div>
            </div>
            <div style={{ marginTop: 12 }}>
              <div className="mono" style={{ color: "#94a3b8", fontSize: 11 }}>Scope of works</div>
              <div data-testid="detail-scope" style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.55 }}>{intake.scope_of_works || "—"}</div>
            </div>
          </div>

          <h2 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569" }}>
            Selected hazards ({(rams.selected_hazards || []).length}) — {(rams.review_required_hazard_ids || []).length} flagged for review
          </h2>
          <HazardsTable rams={rams} />

          {(rams.permits_proposed || []).length ? (
            <>
              <h2 style={{ margin: "24px 0 8px", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569" }}>Permits proposed</h2>
              <div className="card" style={{ padding: 0 }} data-testid="detail-permits">
                <table>
                  <thead><tr><th>Permit</th><th>Trigger</th></tr></thead>
                  <tbody>{(rams.permits_proposed || []).map((p, i) => <tr key={i}><td style={{ fontWeight: 600 }}>{p.permit}</td><td className="mono" style={{ fontSize: 11 }}>{p.trigger}</td></tr>)}</tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>

        {/* Sign-off rail */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="signoff-rail">
          {/* Stage 1 */}
          {status === "Draft" ? (
            <Stage1Panel rams={rams} onSigned={(d) => setRAMS(d)} />
          ) : (
            <SigBlock
              title="Stage 1 — On-site authorisation"
              sub={[["Signer", rams.stage1_sign?.operative_name], ["Competence", rams.stage1_sign?.competence_basis], ["At", rams.stage1_sign?.at?.slice(0, 19).replace("T", " ")]]}
              sig={rams.stage1_sign?.signature_data_url}
            />
          )}
          {/* Stage 2 */}
          {status === "Stage1Signed" ? (
            <Stage2Panel rams={rams} onReviewed={(d) => setRAMS(d)} role={user?.role} />
          ) : status === "Draft" ? null : (
            <SigBlock
              title="Stage 2 — Competent-person review"
              sub={[["Reviewer", rams.stage2_review?.name], ["Role", rams.stage2_review?.role], ["Comment", rams.stage2_review?.comment], ["At", rams.stage2_review?.at?.slice(0, 19).replace("T", " ")]]}
              sig={rams.stage2_review?.signature_data_url}
            />
          )}
          {/* DRA */}
          {status === "Stage2Approved" ? (
            <DRAPanel rams={rams} onSigned={(d) => setRAMS(d)} role={user?.role} />
          ) : status === "DRASigned" ? (
            <SigBlock
              title={`DRA — McCain (${rams.dra_sign?.type})`}
              sub={[["Signer", rams.dra_sign?.by_name], ["Comment", rams.dra_sign?.comment], ["At", rams.dra_sign?.at?.slice(0, 19).replace("T", " ")]]}
              sig={rams.dra_sign?.type === "pad" ? rams.dra_sign?.signature_data_url : null}
            />
          ) : null}

          {status === "DRASigned" ? (
            <div className="card" style={{ padding: 12, background: "#ecfdf5", borderLeft: "4px solid #059669" }} data-testid="dra-signed-banner">
              <div className="mono" style={{ fontSize: 11, color: "#065f46", lineHeight: 1.55 }}>
                <CheckCircle2 size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
                <b>RAMS fully signed.</b> Structure {rams.structure_description?.slice(0, 30)} can now be moved to <b>Erected</b>.
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
